import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useGetEvalRunsQuery,
  useGetEvalFilterOptionsQuery,
} from "../../../app/api/console/evalResultsApiSlice";
import HistoryChart from "./HistoryChart";
import {
  verdictCfg,
  RUN_KIND_LABELS,
  formatMetric,
  formatUsd,
  timeAgo,
} from "./evalConfig";

export function VerdictBadge({ verdict }) {
  const cfg = verdictCfg(verdict);
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${cfg.bg} ${cfg.text} ${cfg.border}`}>
      {cfg.label}
    </span>
  );
}

export function CellsBar({ passed, failed }) {
  const p = Number(passed) || 0;
  const f = Number(failed) || 0;
  const total = p + f;
  if (!total) return <span className='text-xs text-gray-400'>—</span>;
  const pct = Math.round((p / total) * 100);
  return (
    <div className='flex items-center gap-2 min-w-[90px]'>
      <div className='flex-1 h-1.5 rounded-full bg-gray-200 overflow-hidden'>
        <div className='h-full bg-gray-800' style={{ width: `${pct}%` }} />
      </div>
      <span className='text-[11px] text-gray-600 whitespace-nowrap'>
        {p}/{total}
      </span>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      title={label}
      className='text-sm border border-gray-300 rounded-lg px-2.5 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500'>
      <option value=''>{label}: all</option>
      {options.map((o) => (
        <option key={o.value ?? o} value={o.value ?? o}>
          {o.label ?? o}
        </option>
      ))}
    </select>
  );
}

function EvalResults() {
  const navigate = useNavigate();

  const [brand, setBrand] = useState("");
  const [agentType, setAgentType] = useState("");
  const [runKind, setRunKind] = useState("");
  const [packVersion, setPackVersion] = useState("");
  const [textFilter, setTextFilter] = useState("");
  // History chart subject — defaults to the newest filtered run's tuple.
  const [chartSubject, setChartSubject] = useState(null);

  const { data: optionsData } = useGetEvalFilterOptionsQuery();
  const { data, isLoading, isFetching, error, refetch } = useGetEvalRunsQuery({
    brand: brand || undefined,
    agentType: agentType || undefined,
    runKind: runKind || undefined,
    packVersion: packVersion || undefined,
    limit: 200,
  });

  const runs = useMemo(() => {
    let list = data?.runs || [];
    if (textFilter.trim()) {
      const q = textFilter.trim().toLowerCase();
      list = list.filter((r) =>
        [r.run_id, r.suite_id, r.website_id, r.brand, r.agent_type, r.ticket, r.operator]
          .filter(Boolean)
          .some((f) => String(f).toLowerCase().includes(q))
      );
    }
    return list;
  }, [data, textFilter]);

  const effectiveSubject = useMemo(() => {
    if (chartSubject) return chartSubject;
    const first = runs.find((r) => r.website_id && r.agent_type);
    return first
      ? { websiteId: first.website_id, agentType: first.agent_type, brand: first.brand }
      : null;
  }, [chartSubject, runs]);

  const tableMissing = data?.table_missing;

  return (
    <div className='w-full max-w-none'>
      {/* Header */}
      <div className='flex items-start justify-between mb-5'>
        <div>
          <h1 className='text-xl font-semibold text-gray-900'>Eval Results</h1>
          <p className='text-sm text-gray-500 mt-1'>
            Pushed eval runs with full provenance (skill, memory, pack, model) —
            per-cell verdicts and the TCQ / precision / recall history per agent.
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className='flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors'>
          <svg
            className={`w-3.5 h-3.5 ${isFetching ? "animate-spin" : ""}`}
            fill='none'
            viewBox='0 0 24 24'
            stroke='currentColor'
            strokeWidth={2}>
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              d='M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15'
            />
          </svg>
          Refresh
        </button>
      </div>

      {/* Filter bar */}
      <div className='flex flex-wrap items-center gap-2 mb-4'>
        <FilterSelect
          label='Brand'
          value={brand}
          onChange={setBrand}
          options={optionsData?.brands || []}
        />
        <FilterSelect
          label='Agent type'
          value={agentType}
          onChange={setAgentType}
          options={optionsData?.agent_types || []}
        />
        <FilterSelect
          label='Run kind'
          value={runKind}
          onChange={setRunKind}
          options={(optionsData?.run_kinds || []).map((k) => ({
            value: k,
            label: RUN_KIND_LABELS[k] || k,
          }))}
        />
        <FilterSelect
          label='Pack'
          value={packVersion}
          onChange={setPackVersion}
          options={optionsData?.pack_versions || []}
        />
        <input
          value={textFilter}
          onChange={(e) => setTextFilter(e.target.value)}
          placeholder='Filter by run id, suite, website, ticket…'
          className='flex-1 min-w-[220px] text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500'
        />
        <span className='text-xs text-gray-400'>
          {runs.length} run{runs.length === 1 ? "" : "s"}
        </span>
      </div>

      {error && (
        <div className='mb-4 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700'>
          Failed to load eval runs: {error?.data?.detail || error?.error || "unknown error"}
        </div>
      )}
      {tableMissing && (
        <div className='mb-4 px-4 py-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-700'>
          The eval results store is not provisioned yet (no{" "}
          <span className='font-mono'>eval-runs</span> table found). Runs will
          appear here once the first result is pushed.
        </div>
      )}

      {/* History chart */}
      <div className='bg-white border border-gray-200 rounded-xl p-4 mb-4'>
        <div className='flex items-center justify-between mb-2'>
          <h2 className='text-sm font-semibold text-gray-900'>
            Metric history
            {effectiveSubject && (
              <span className='ml-2 font-normal text-gray-500'>
                {effectiveSubject.brand ? `${effectiveSubject.brand} · ` : ""}
                {effectiveSubject.agentType} ·{" "}
                <span className='font-mono text-xs'>{effectiveSubject.websiteId}</span>
              </span>
            )}
          </h2>
          {chartSubject && (
            <button
              onClick={() => setChartSubject(null)}
              className='text-xs text-indigo-600 hover:text-indigo-800'>
              Reset to newest run
            </button>
          )}
        </div>
        <HistoryChart
          websiteId={effectiveSubject?.websiteId}
          agentType={effectiveSubject?.agentType}
        />
        <p className='text-[11px] text-gray-400 mt-1'>
          Click a run row's chart icon to plot that website + agent.
        </p>
      </div>

      {/* Runs table */}
      <div className='bg-white border border-gray-200 rounded-xl overflow-x-auto'>
        <table className='w-full text-sm'>
          <thead>
            <tr className='bg-gray-50 text-left text-[11px] uppercase tracking-wide text-gray-500 border-b border-gray-200'>
              <th className='px-4 py-2.5 font-semibold'>Started</th>
              <th className='px-4 py-2.5 font-semibold'>Run</th>
              <th className='px-4 py-2.5 font-semibold'>Suite</th>
              <th className='px-4 py-2.5 font-semibold'>Kind</th>
              <th className='px-4 py-2.5 font-semibold'>Gate</th>
              <th className='px-4 py-2.5 font-semibold'>Cells</th>
              <th className='px-4 py-2.5 font-semibold'>P</th>
              <th className='px-4 py-2.5 font-semibold'>R</th>
              <th className='px-4 py-2.5 font-semibold'>TCQ</th>
              <th className='px-4 py-2.5 font-semibold'>Cost</th>
              <th className='px-4 py-2.5 font-semibold'>Verdict</th>
              <th className='px-4 py-2.5 font-semibold'>Pack</th>
              <th className='px-4 py-2.5 font-semibold'></th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={13} className='px-4 py-10 text-center text-gray-400'>
                  Loading eval runs…
                </td>
              </tr>
            ) : runs.length === 0 ? (
              <tr>
                <td colSpan={13} className='px-4 py-10 text-center text-gray-400'>
                  No eval runs match the current filters.
                </td>
              </tr>
            ) : (
              runs.map((r) => (
                <tr
                  key={r.run_id}
                  onClick={() => navigate(`/admin/eval-results/runs/${r.run_id}`)}
                  className='border-b border-gray-100 last:border-0 hover:bg-indigo-50/40 cursor-pointer transition-colors'>
                  <td className='px-4 py-2.5 text-gray-600 whitespace-nowrap' title={r.started_at}>
                    {timeAgo(r.started_at)}
                  </td>
                  <td className='px-4 py-2.5'>
                    <div className='font-mono text-xs text-gray-800 max-w-[180px] truncate' title={r.run_id}>
                      {r.run_id}
                    </div>
                    <div className='text-[11px] text-gray-400'>
                      {r.brand || r.website_id || "—"}
                      {r.agent_type ? ` · ${r.agent_type}` : ""}
                    </div>
                  </td>
                  <td className='px-4 py-2.5 text-gray-600 max-w-[160px] truncate' title={r.suite_id}>
                    {r.suite_id || "—"}
                  </td>
                  <td className='px-4 py-2.5 text-gray-600 whitespace-nowrap'>
                    {RUN_KIND_LABELS[r.run_kind] || r.run_kind || "—"}
                  </td>
                  <td className='px-4 py-2.5 text-gray-600'>{r.gate_label || "—"}</td>
                  <td className='px-4 py-2.5'>
                    <CellsBar passed={r.cells_passed} failed={r.cells_failed} />
                  </td>
                  <td className='px-4 py-2.5 text-gray-700 tabular-nums'>
                    {formatMetric(r.median_precision)}
                  </td>
                  <td className='px-4 py-2.5 text-gray-700 tabular-nums'>
                    {formatMetric(r.median_recall)}
                  </td>
                  <td className='px-4 py-2.5 font-semibold text-gray-900 tabular-nums'>
                    {formatMetric(r.median_tcq)}
                  </td>
                  <td className='px-4 py-2.5 text-gray-600 whitespace-nowrap'>
                    {formatUsd(r.total_cost_usd)}
                  </td>
                  <td className='px-4 py-2.5'>
                    <VerdictBadge verdict={r.verdict} />
                  </td>
                  <td className='px-4 py-2.5 text-gray-600 font-mono text-xs'>
                    {r.pack_version || "—"}
                  </td>
                  <td className='px-4 py-2.5'>
                    {r.website_id && r.agent_type && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setChartSubject({
                            websiteId: r.website_id,
                            agentType: r.agent_type,
                            brand: r.brand,
                          });
                        }}
                        title='Plot history for this website + agent'
                        className='p-1 rounded-md text-gray-400 hover:text-indigo-600 hover:bg-indigo-50'>
                        <svg className='w-4 h-4' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={1.8}>
                          <path strokeLinecap='round' strokeLinejoin='round' d='M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941' />
                        </svg>
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default EvalResults;
